import { Table, Tag, Space, Flex } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import * as React from 'react'
import { WithdrawalEdit } from '../../edit'

type Props = {
  withdrawal: WithdrawalEdit
}

type UiCheckRow = {
  key: number
  name: string
  shortName: string
  result:
    | 'success'
    | 'failed'
    | 'error'
    | 'pending'
    | 'skipped'
    | 'running'
    | 'awaiting callback'
    | 'unspecified'
  resultLabel: string
  details?: string
}

const CHECK_META: Record<number, { name: string; shortName: string }> = {
  7: { name: '2FA включено', shortName: '2FA' },
  1: { name: 'Проверка на читинг от диллеров', shortName: 'Dealing' },
  10: {
    name: 'Условия торговли: минимальное количество сделок',
    shortName: 'Minimum trade',
  },
  6: {
    name: 'Условия торговли: компенсация комиссий',
    shortName: 'Trading and payment fees',
  },
  8: {
    name: 'Разрешение клиента на автовыводы',
    shortName: 'Autowithdrawal are allowed',
  },
  3: {
    name: 'Максимальная сумма автовывода',
    shortName: 'Max autowithdrawal amount',
  },
  4: { name: 'Допустимая для вывода сумма', shortName: 'Client purse amount' },
  2: { name: 'Проверка на число выводов', shortName: 'Daily withdrawal count' },
  9: { name: 'Проверка категорий клиента', shortName: 'Client category' },
  11: {
    name: 'Наличие незакрытых депозитов с карт',
    shortName: 'Card priority',
  },
  12: {
    name: 'Наличие незакрытых депозитов от третьих лиц',
    shortName: '3rd party check',
  },
  13: {
    name: 'Соответствие методу последнего депозита (LIFO/Крипта)',
    shortName: 'LIFO & Crypto',
  },
}

// enum статусов CHECK из бэкенда маппим в UI-результат
const mapCheckStatusToResult = (status: number): UiCheckRow['result'] => {
  switch (status) {
    case 1: // PENDING
      return 'pending'
    case 2: // RUNNING
      return 'running'
    case 3: // AWAITING_CALLBACK
      return 'awaiting callback'
    case 4: // PASSED
      return 'success'
    case 5: // FAILED
      return 'failed'
    case 6: // ERROR
      return 'error'
    case 7: // SKIPPED
      return 'skipped'
    case 0: // UNSPECIFIED
    default:
      return 'unspecified'
  }
}

type BackendDecision = WithdrawalEdit['withdrawalDecisions']
type BackendCheck = NonNullable<BackendDecision>['checks'][number]

const formatDetails = (d: BackendCheck): string | undefined => {
  switch (d.checkType) {
    default:
      return d.details || d.error || '-'
  }
}

const resultTag = (r: UiCheckRow['result']) => {
  switch (r) {
    case 'success':
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          Passed
        </Tag>
      )
    case 'failed':
      return (
        <Tag color="red" icon={<CloseCircleOutlined />}>
          Failed
        </Tag>
      )
    case 'error':
      return (
        <Tag color="orange" icon={<ExclamationCircleOutlined />}>
          Error
        </Tag>
      )
    case 'skipped':
      return (
        <Tag color="default" icon={<ExclamationCircleOutlined />}>
          Skipped
        </Tag>
      )
    case 'pending':
    default:
      return (
        <Tag color="default" icon={<ClockCircleOutlined />}>
          Pending
        </Tag>
      )
  }
}

// статус решения (0–3) + флаг approved -> общий итог
const mapDecisionStatusToOverall = (
  status?: number,
  approved?: boolean | null,
): 'yes' | 'no' | 'pending' | 'error' => {
  // COMPLETED
  if (status === 2) {
    if (approved === true) return 'yes'
    if (approved === false) return 'no'
    return 'error'
  }

  // UNSPECIFIED / CREATED / RUNNING
  if (status === 0 || status === 1) {
    return 'pending'
  }

  return 'error'
}

const overallTag = (state: 'yes' | 'no' | 'pending' | 'error') => {
  switch (state) {
    case 'yes':
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          Yes
        </Tag>
      )
    case 'no':
      return (
        <Tag color="red" icon={<CloseCircleOutlined />}>
          No
        </Tag>
      )
    case 'error':
      return (
        <Tag color="orange" icon={<ExclamationCircleOutlined />}>
          Error
        </Tag>
      )
    case 'pending':
    default:
      return (
        <Tag color="default" icon={<ClockCircleOutlined />}>
          Pending
        </Tag>
      )
  }
}

export const AutoCheck = ({ withdrawal }: Props) => {
  const decision = withdrawal.withdrawalDecisions
  const checks = decision?.checks || []

  const rows: UiCheckRow[] = checks
    .sort((a, b) => a.checkType - b.checkType)
    .map((d) => {
      const meta = CHECK_META[d.checkType] || {
        name: `Check type ${d.checkType}`,
        shortName: `Type ${d.checkType}`,
      }
      const result = mapCheckStatusToResult(d.status)

      return {
        key: d.decisionCheckID,
        name: meta.name,
        shortName: meta.shortName,
        result,
        resultLabel: result,
        details: formatDetails(d),
      }
    })

  const overallState = mapDecisionStatusToOverall(
    decision?.status,
    decision?.approved,
  )


  return (
    <Flex gap="middle" vertical>
      <Space>
        <span className="fw-semibold">Automatic withdrawal is allowed:</span>
        {overallTag(overallState)}
      </Space>
      <Table<UiCheckRow>
        size="small"
        bordered
        pagination={false}
        dataSource={rows}
        columns={[
          {
            title: 'Check',
            dataIndex: 'shortName',
            key: 'name',
            render: (_value, record) => (
              <span title={record.name}>{record.shortName}</span>
            ),
          },
          {
            title: 'Result',
            dataIndex: 'result',
            key: 'result',
            align: 'center',
            render: (value) => resultTag(value),
          },
          {
            title: 'Details',
            dataIndex: 'details',
            key: 'details',
            render: (value) => value || '-',
          },
        ]}
      />
    </Flex>
  )
}
